<template>
  <section id="technicians">
    <div class="section-container">
      <div class="a-header-container">
        <div class="title-container">
          <div class="page-label">
            Technicians
          </div>
          <button
            v-if="allTechnicians.length > 0 && adminRole === 'admin'"
            type="button"
            class="invite-tech-btn"
            @click="toggleActionModal(true, 'invite-tech')"
          >
            <img
              alt="plus-icon"
              class="plus-icon"
              src="@shared/assets/img/general/plus-white.svg"
            >
            <span class="invite-tech-btn-txt">
              Add a Technician
            </span>
          </button>
        </div>
        <div class="a-right-container">
          <div
            v-if="allTechnicians.length > 0 && adminRole === 'admin'"
            class="export-container"
            @click="toggleActionModal(true, 'bulk-tech', {})"
            @keydown="toggleActionModal(true, 'bulk-tech', {})"
          >
            <button
              type="button"
              class="export-button"
            >
              IMPORT
            </button>
            <div class="export-label-container">
              <span class="export-label">
                CSV
              </span>
            </div>
          </div>
          <div
            v-if="allTechnicians.length > 0"
            class="export-container"
            @click="exportInstallationCSV"
            @keydown="exportInstallationCSV"
          >
            <button
              type="button"
              class="export-button"
            >
              <span v-if="!exporting">
                EXPORT
              </span>
              <span v-else>
                EXPORTING
                <img
                  src="@shared/assets/img/general/loading-white.svg"
                  alt="Exporting"
                >
              </span>
            </button>
            <div class="export-label-container">
              <span class="export-label">
                CSV
              </span>
            </div>
          </div>
          <div>
            <SACSearch
              v-if="!showActionModal"
              darkMode
              :width="200"
              :height="40"
              :search="search"
              placeholder="Search"
              @input="(value) => search = value.trim()"
            />
          </div>
          <SelectedPartner />
        </div>
      </div>
      <div
        v-if="initialLoading"
        class="loading-container"
      >
        <img
          src="@shared/assets/img/general/loading-white.svg"
          alt="Loading Technicians"
          class="loader"
        >
      </div>
      <div
        v-else-if="allTechnicians.length === 0"
        class="empty-container"
      >
        <div class="t-header">
          Start Installing Today!
        </div>
        <div class="flex-row align-center">
          <img
            src="@shared/assets/img/general/pro-app-logo.svg"
            alt="pro-app-logo"
            class="pro-app-logo"
          >
          <div class="t-body">
            Invite your technicians to the SmartAC.com
            <br>
            PRO App to see more details
          </div>
        </div>
        <div class="t-image-container">
          <img
            class="truck-img"
            src="@shared/assets/img/technician/truck.png"
            alt="Technician Work Truck"
          >
        </div>
        <div
          v-if="adminRole === 'admin'"
          class="t-button-container"
        >
          <button
            type="button"
            class="t-button"
            @click="toggleModal(true, 'invite-tech')"
          >
            Invite a tech
          </button>
          <button
            type="button"
            class="t-button"
            @click="toggleActionModal(true, 'bulk-tech', {})"
          >
            Import from .CSV
          </button>
        </div>
      </div>
      <div
        v-else
        class="body-container"
      >
        <SACFilters
          :filters="technicianFilters"
          :change="updateFilters"
          :clear="clearFilter"
          :loading="loading"
        />
        <TechnicianList
          :search="search"
          :loading="loading"
          :resendInvite="resendInvite"
        />
      </div>
    </div>
    <ActionModal />
  </section>
</template>

<script>
import adminMixin from '@shared/mixins/adminMixin';
import technicianMixin from '@shared/mixins/technicianMixin';

import SACFilters from '@shared/components/sac/SACFilters.vue';
import SACSearch from '@shared/components/sac/SACSearch.vue';
import TechnicianList from '@shared/components/technician/TechnicianList.vue';
import ActionModal from '@shared/components/technician/ActionModal.vue';

import exportToFile from '@shared/lib/util/exportToFile';
import SelectedPartner from '@shared/components/admin/SelectedPartner.vue';

export default {
  name: 'TechnicianView',
  metaInfo: {
    title: 'SmartAC.com | Technician',
  },
  components: {
    SelectedPartner,
    SACFilters,
    SACSearch,
    TechnicianList,
    ActionModal,
  },
  mixins: [adminMixin, technicianMixin],
  data() {
    return {
      search: '',
      showSearch: false,
      loading: true,
      initialLoading: true,
      exporting: false,
    };
  },
  watch: {
    selectedCorpId() {
      this.initialLoad();
    },
    technicianFilters() {
      if (!this.loading) {
        this.getTechnicianList();
      }
    },
  },
  async mounted() {
    this.initialLoad();
    this.$root.$on('updateAdminFilter', async () => {
      this.clearFilter('techs');
    });
  },
  methods: {
    async initialLoad() {
      const {
        query: {
          technician: techId,
        },
      } = this.$route;
      await this.$store.dispatch('getTechniciansForFilter', techId);
      await this.$store.dispatch('getPartnerSettings');
      this.getTechnicianList();
    },
    updateFilters(filters) {
      this.$store.dispatch('changeTechnicianValue', {
        field: 'filters',
        value: filters,
      });
    },
    clearFilter(key) {
      const newFilters = this.technicianFilters.map((filter) => {
        if (filter.key === key) {
          filter.active = false;
          filter.activeOption = {};
        }
        return filter;
      });
      this.updateFilters(newFilters);
    },
    toggleModal(show, view) {
      this.toggleActionModal(show, view);
    },
    focusInput() {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    },
    async getTechnicianList() {
      this.loading = true;
      await this.$store.dispatch('getTechnicianList');
      this.loading = false;
      this.initialLoading = false;
    },
    resendInvite(technician) {
      this.$store.dispatch('resendTechInvite', technician);
    },
    async exportInstallationCSV() {
      this.exporting = true;
      try {
        const data = await this.$store.dispatch('exportTechnicianList');
        if (data) {
          exportToFile({
            data,
            filename: 'Technician List.csv',
          });
          this.$store.dispatch('showSuccessToast', {
            header: 'Export was successful.',
            message: 'technician list was download as .CSV',
          });
        } else {
          this.$store.dispatch('showAlertToast', {
            header: 'There was an unexpected error exporting technician list.',
            message: 'Please try again or contact support',
          });
        }
      } catch (err) {
        console.error(err);
      }
      this.exporting = false;
    },
  },
};
</script>

<style lang="scss" scoped>
section#technicians {
  display: flex;
  flex: 1;
  background: $BLACK;
  letter-spacing: 0.2px;
  justify-content: center;
  padding: 52px 100px 36px;

  @media (max-width: 1279px) {
    padding: 52px 60px 0;
  }

  @media (max-width: 1023px) {
    padding: 52px 40px 0;
  }

  .section-container {
    display: flex;
    flex-direction: column;
    width: 1300px;

    @media (max-width: 1280px) {
      width: 100%;
      font-size: 90%;
      gap: 12px;
    }

    @media (max-width: 1023px) {
      font-size: 80%;
    }

    @media (max-width: 850px) {
      font-size: 70%;
    }

    .plus-icon {
      width: 27px;
      margin-right: 5px;
    }

    .invite-tech-btn {
      display: flex;
      flex-direction: row;
      align-items: center;
      border-radius: 8px;
      background-color: $N95;
      width: 190px;
      height: 40px;
      color: white;
      padding: 5px;
    }

    .invite-tech-btn-txt {
      font-size: 15px;
      font-weight: 600;
    }

    .title-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }

    .a-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .a-right-container {
        display: flex;
        align-items: center;
        gap: 16px;
        .export-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;

          &:hover {
            .export-label-container {
              display: block;
            }
          }

          .export-label-container {
            display: none;
            position: absolute;
            background-color: $WHITE;
            top: 45px;
            padding: 7px 16px 7px 16px;
            box-shadow: 0px 3px 6px rgba(9, 15, 32, 0.07);
            border-radius: 6px;
            white-space: nowrap;
            z-index: 5;
            cursor: pointer;

            &:hover {
              display: block;
              background-color: $SUNSET100;

              &:after {
                border-bottom-color: $SUNSET100;
              }
            }

            &:after {
              content: '';
              position: absolute;
              top: 0;
              left: 50%;
              width: 0;
              height: 0;
              border: 8px solid transparent;
              border-bottom-color: $WHITE;
              border-top: 0;
              margin-left: -8px;
              margin-top: -8px;
            }

            .export-label {
              width: 100%;
              font-size: 0.875rem;
              color: $BLACK;
            }
          }

          .export-button {
            height: 38px;
            padding: 0 8px;
            color: $N50;
            display: flex;
            align-items: center;
            white-space: nowrap;
            font-size: 0.875rem;
            font-weight: 700;
            border: 1px solid $N50;
            border-radius: 8px;
            line-height: 1;

            span {
              height: 100%;
              display: flex;
              align-items: center;
            }

            img {
              height: 40%;
              margin-left: 8px;
              filter: invert(50%);
            }
          }
        }
      }
    }

    .empty-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: $WHITE;

      .t-header {
        font-size: 2rem;
        font-weight: 600;
        margin-bottom: 12px;
      }

      .t-body {
        font-weight: 500;
        font-size: 1rem;
        margin-left: 16px;
      }

      .t-image-container {
        margin-top: 42px;
        margin-bottom: 28px;
        position: relative;

        .truck-img {
          width: 490px;
        }
      }

      .t-button-container {
        .t-button {
          padding: 16px 24px;
          font-weight: 700;
          border-radius: 8px;

          &:first-child {
            margin-right: 24px;
            background-color: $N50;
          }

          &:last-child {
            background-color: $NBLUE100;
          }
        }
      }
    }
    .loading-container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
    }
  }
}
</style>
